import { valueToBigNumber } from '@aave/math-utils';
import { Trans } from '@lingui/macro';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ConnectWalletPaper } from 'src/components/ConnectWalletPaper';
import { ArrowUpRightIcon } from 'src/components/icons/ArrowUpRightIcon';
import { LIST_CARDS_BELOW } from 'src/components/lists/listBreakpoints';
import { ListColumn } from 'src/components/lists/ListColumn';
import { ListHeaderTitle } from 'src/components/lists/ListHeaderTitle';
import { ListHeaderWrapper } from 'src/components/lists/ListHeaderWrapper';
import { ListItem } from 'src/components/lists/ListItem';
import { ListWrapper } from 'src/components/lists/ListWrapper';
import { FormattedNumber } from 'src/components/primitives/FormattedNumber';
import { Link, ROUTES } from 'src/components/primitives/Link';
import { TokenIcon } from 'src/components/primitives/TokenIcon';
import { useAppDataContext } from 'src/hooks/app-data-provider/useAppDataProvider';
import { useWalletBalances } from 'src/hooks/app-data-provider/useWalletBalances';
import { useModalContext } from 'src/hooks/useModal';
import { useWeb3Context } from 'src/libs/hooks/useWeb3Context';
import { useRootStore } from 'src/store/root';

import { FaucetItemLoader } from './FaucetItemLoader';

export default function FaucetAssetsList() {
  const { reserves, loading } = useAppDataContext();
  const { openFaucet } = useModalContext();
  const { currentAccount } = useWeb3Context();
  const currentMarket = useRootStore((store) => store.currentMarket);
  const currentMarketData = useRootStore((store) => store.currentMarketData);
  const { walletBalances } = useWalletBalances(currentMarketData);

  const theme = useTheme();
  const showCards = useMediaQuery(theme.breakpoints.down(LIST_CARDS_BELOW));

  const listData = reserves
    .filter(
      (reserve) => !reserve.isWrappedBaseAsset && !reserve.isFrozen && reserve.symbol !== 'GHO'
    )
    .map((reserve) => {
      const walletBalance = valueToBigNumber(
        walletBalances[reserve.underlyingAsset]?.amount || '0'
      );
      return {
        ...reserve,
        walletBalance,
      };
    });

  if (!currentAccount) {
    return (
      <ConnectWalletPaper
        description={<Trans>Please connect your wallet to get free testnet assets.</Trans>}
      />
    );
  }

  return (
    <ListWrapper
      titleComponent={
        <Typography component="div" variant="h2" sx={{ mr: 4 }}>
          <Trans>Test Assets</Trans>
        </Typography>
      }
    >
      <ListHeaderWrapper px={showCards ? 4 : 5}>
        <ListColumn isRow maxWidth={280}>
          <ListHeaderTitle>
            <Trans>Asset</Trans>
          </ListHeaderTitle>
        </ListColumn>

        {!showCards && (
          <ListColumn>
            <ListHeaderTitle>
              <Trans>Wallet balance</Trans>
            </ListHeaderTitle>
          </ListColumn>
        )}

        <ListColumn maxWidth={280} />
      </ListHeaderWrapper>

      {loading ? (
        showCards ? (
          <>
            <FaucetItemLoader compact />
            <FaucetItemLoader compact />
            <FaucetItemLoader compact />
          </>
        ) : (
          <>
            <FaucetItemLoader />
            <FaucetItemLoader />
            <FaucetItemLoader />
            <FaucetItemLoader />
            <FaucetItemLoader />
          </>
        )
      ) : (
        listData.map((reserve) => (
          <ListItem
            px={showCards ? 4 : 5}
            key={reserve.symbol}
            data-cy={`faucetListItem_${reserve.symbol.toUpperCase()}`}
          >
            <ListColumn isRow maxWidth={280}>
              <Link
                href={ROUTES.reserveOverview(reserve.underlyingAsset, currentMarket)}
                noWrap
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <TokenIcon symbol={reserve.iconSymbol} fontSize="large" />
                <Box sx={{ pl: 3.5, overflow: 'hidden' }}>
                  <Typography variant="h4" noWrap>
                    {reserve.name}
                  </Typography>
                  <Typography variant="subheader2" color="fg-3" noWrap>
                    {reserve.symbol}
                  </Typography>
                </Box>
              </Link>
            </ListColumn>

            {!showCards && (
              <ListColumn>
                <FormattedNumber compact value={reserve.walletBalance.toString()} variant="h4" />
              </ListColumn>
            )}

            <ListColumn align="right" maxWidth={280}>
              {!currentMarketData.addresses.FAUCET ? (
                <Button
                  href="https://faucet.circle.com/"
                  component={Link}
                  variant="contained"
                  endIcon={<ArrowUpRightIcon sx={{ width: 14, height: 14 }} />}
                >
                  <Trans>Faucet</Trans>
                </Button>
              ) : (
                <Button variant="contained" onClick={() => openFaucet(reserve.underlyingAsset)}>
                  <Trans>Faucet</Trans>
                </Button>
              )}
            </ListColumn>
          </ListItem>
        ))
      )}
    </ListWrapper>
  );
}
